import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "./ui/dialog"
import { Button } from "./ui/button"
import { Progress } from "./ui/progress"
import { Alert, AlertDescription } from "./ui/alert"
import { Card, CardContent } from "./ui/card"
import folderService from '../services/folderService';
import fileService from '../services/fileService';
import { formatFileSize } from '../utils/helpers';
import {
    FolderUp,
    FolderOpen,
    File,
    ChevronRight,
    ChevronDown,
    Loader2,
    AlertTriangle
} from 'lucide-react';
import { cn } from "../lib/utils"

const FolderUpload = ({ open, onClose, onUploadSuccess, parentId }) => {
    const [selectedFolder, setSelectedFolder] = useState(null);
    const [folderStructure, setFolderStructure] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [error, setError] = useState('');
    const [storageInfo, setStorageInfo] = useState(null);
    const [loadingStorage, setLoadingStorage] = useState(true);
    const [expandedFolders, setExpandedFolders] = useState(new Set());

    useEffect(() => {
        if (open) {
            loadStorageInfo();
            setSelectedFolder(null);
            setFolderStructure(null);
            setError('');
        }
    }, [open]);

    const loadStorageInfo = async () => {
        try {
            setLoadingStorage(true);
            const data = await fileService.getStorageStats();
            setStorageInfo(data.storage);
        } catch (err) {
            console.error('Error loading storage info:', err);
        } finally {
            setLoadingStorage(false);
        }
    };

    const buildFolderStructure = (files) => {
        const structure = {};
        let totalSize = 0;

        files.forEach(file => {
            totalSize += file.size;
            const path = file.webkitRelativePath || file.name;
            const parts = path.split('/');

            let current = structure;
            parts.forEach((part, index) => {
                if (index === parts.length - 1) {
                    if (!current._files) current._files = [];
                    current._files.push({ name: part, size: file.size });
                } else {
                    if (!current[part]) {
                        current[part] = {};
                    }
                    current = current[part];
                }
            });
        });

        return { structure, totalSize, fileCount: files.length };
    };

    const handleFolderSelect = (event) => {
        const files = Array.from(event.target.files);
        setError('');

        if (files.length === 0) return;

        const { structure, totalSize, fileCount } = buildFolderStructure(files);

        if (storageInfo) {
            const { available } = storageInfo;
            if (totalSize > available) {
                setError('Storage limit exceeded! Ukuran folder melebihi penyimpanan tersedia.');
                return;
            }
        }

        setSelectedFolder({ files, structure, totalSize, fileCount });
        setFolderStructure(structure);
    };

    const handleUpload = async () => {
        if (!selectedFolder) {
            setError('Harap pilih folder');
            return;
        }

        try {
            setUploading(true);
            setError('');
            setUploadProgress(0);

            await folderService.uploadFolder(
                selectedFolder.files,
                parentId,
                (progress) => setUploadProgress(progress)
            );

            setSelectedFolder(null);
            setFolderStructure(null);
            setUploadProgress(0);
            onUploadSuccess();
            onClose();
        } catch (err) {
            console.error('Upload error:', err);
            if (err.response?.status === 400 && err.response?.data?.storageInfo) {
                setError('Storage limit exceeded!');
            } else {
                setError(err.response?.data?.message || 'Gagal mengunggah folder');
            }
        } finally {
            setUploading(false);
        }
    };

    const handleClose = () => {
        if (!uploading) {
            setSelectedFolder(null);
            setFolderStructure(null);
            setUploadProgress(0);
            setError('');
            onClose();
        }
    };

    const getStoragePercentage = () => {
        if (!storageInfo) return 0;
        return (storageInfo.used / storageInfo.limit) * 100;
    };

    const toggleFolder = (path) => {
        const newExpanded = new Set(expandedFolders);
        if (newExpanded.has(path)) {
            newExpanded.delete(path);
        } else {
            newExpanded.add(path);
        }
        setExpandedFolders(newExpanded);
    };

    const renderFolderTree = (structure, path = '', level = 0) => {
        const items = [];

        Object.keys(structure).forEach(key => {
            if (key === '_files') {
                structure[key].forEach((file, index) => {
                    items.push(
                        <div
                            key={`${path}/file-${index}`}
                            className="flex items-center py-1 text-sm text-gray-600 hover:bg-gray-100 rounded px-2"
                            style={{ paddingLeft: `${(level + 1) * 16}px` }}
                        >
                            <File className="h-4 w-4 mr-2 text-gray-400" />
                            <span className="truncate">{file.name}</span>
                            <span className="ml-auto text-xs text-gray-400">{formatFileSize(file.size)}</span>
                        </div>
                    );
                });
            } else {
                const currentPath = path ? `${path}/${key}` : key;
                const isExpanded = expandedFolders.has(currentPath);

                items.push(
                    <div key={currentPath}>
                        <div
                            className="flex items-center py-1 text-sm font-medium text-gray-800 hover:bg-gray-100 rounded px-2 cursor-pointer"
                            style={{ paddingLeft: `${level * 16}px` }}
                            onClick={() => toggleFolder(currentPath)}
                        >
                            {isExpanded ?
                                <ChevronDown className="h-4 w-4 mr-1 text-gray-400" /> :
                                <ChevronRight className="h-4 w-4 mr-1 text-gray-400" />
                            }
                            <FolderOpen className="h-4 w-4 mr-2 text-yellow-500" />
                            {key}
                        </div>
                        {isExpanded && (
                            <div>
                                {renderFolderTree(structure[key], currentPath, level + 1)}
                            </div>
                        )}
                    </div>
                );
            }
        });

        return items;
    };

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <FolderUp className="h-5 w-5 text-primary" />
                        Upload Folder
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-4 py-2">
                    {/* Storage Info */}
                    {!loadingStorage && storageInfo && (
                        <Card className={cn(
                            "border-2",
                            getStoragePercentage() > 80 ? "border-yellow-400 bg-yellow-50" : "border-gray-200 bg-gray-50"
                        )}>
                            <CardContent className="p-4">
                                <div className="flex justify-between items-center mb-2">
                                    <span className="text-sm font-semibold">Penyimpanan Anda</span>
                                    <span className="text-sm font-bold">{getStoragePercentage().toFixed(1)}%</span>
                                </div>
                                <Progress value={getStoragePercentage()} className="h-2 mb-2" />
                                <div className="flex justify-between text-xs text-muted-foreground">
                                    <span>{formatFileSize(storageInfo.used)} / {formatFileSize(storageInfo.limit)}</span>
                                    <span>Tersedia: {formatFileSize(storageInfo.available)}</span>
                                </div>
                                {getStoragePercentage() > 80 && (
                                    <div className="flex items-center gap-1 mt-2 text-xs text-yellow-700 font-medium">
                                        <AlertTriangle className="h-3 w-3" />
                                        Penyimpanan hampir penuh!
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    )}

                    {error && (
                        <Alert variant="destructive">
                            <AlertDescription>{error}</AlertDescription>
                        </Alert>
                    )}

                    <div className="grid w-full items-center gap-1.5">
                        <input
                            id="folder-upload"
                            type="file"
                            webkitdirectory=""
                            directory=""
                            className="hidden"
                            onChange={handleFolderSelect}
                            disabled={uploading}
                        />
                        <label htmlFor="folder-upload">
                            <Button
                                variant="outline"
                                className="w-full h-20 border-dashed border-2 flex flex-col gap-2"
                                disabled={uploading}
                                asChild
                                type="button"
                                onClick={() => document.getElementById('folder-upload').click()}
                            >
                                <div className="cursor-pointer flex flex-col items-center">
                                    <FolderUp className="h-6 w-6 text-muted-foreground" />
                                    <span>Klik untuk memilih folder</span>
                                </div>
                            </Button>
                        </label>
                    </div>

                    {selectedFolder && (
                        <Card className="border-gray-200 bg-blue-50/20">
                            <CardContent className="p-4">
                                <div className="flex justify-between items-end mb-2">
                                    <span className="font-semibold text-sm">Preview Folder</span>
                                    <span className="text-xs text-muted-foreground">
                                        {selectedFolder.fileCount} Files | {formatFileSize(selectedFolder.totalSize)}
                                    </span>
                                </div>
                                <div className="max-h-[200px] overflow-y-auto border rounded-md bg-white p-2">
                                    {renderFolderTree(folderStructure)}
                                </div>
                                {storageInfo && (
                                    <div className="mt-2 text-xs text-center text-muted-foreground font-medium">
                                        Setelah upload: {formatFileSize(storageInfo.used + selectedFolder.totalSize)} / {formatFileSize(storageInfo.limit)}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    )}

                    {uploading && (
                        <div className="space-y-1">
                            <div className="flex justify-between text-xs">
                                <span>Mengunggah playlist...</span>
                                <span>{uploadProgress}%</span>
                            </div>
                            <Progress value={uploadProgress} className="h-2" />
                        </div>
                    )}
                </div>

                <DialogFooter className="gap-2 sm:gap-0">
                    <Button variant="ghost" onClick={handleClose} disabled={uploading}>
                        Batal
                    </Button>
                    <Button
                        onClick={handleUpload}
                        disabled={!selectedFolder || uploading}
                    >
                        {uploading ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Mengunggah...
                            </>
                        ) : (
                            'Upload Folder'
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default FolderUpload;
